import React, { useMemo, useState } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faPlay, faCheck, faXmark, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { API } from '../../config/api';

const ENG = axios.create({ baseURL: API.BACKEND, timeout: 10000 });
ENG.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});

const TYPE_COLOR = {
  pick: 'success', repack: 'info', putaway: 'primary', move: 'warning', other: 'secondary'
};
const STATUS_COLOR = {
  pending: 'warning', in_progress: 'info', done: 'success', cancelled: 'secondary'
};
const fmt = n => Number(n || 0).toLocaleString();

const TABS = [
  { key: 'open', label: 'Open' },
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'done', label: 'Done' },
  { key: 'all', label: 'All' }
];

export default function WarehouseTasksPage() {
  const [tab, setTab] = useState('open');
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  const { data: tasks = [], isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['warehouse', 'tasks', 'page'],
    queryFn: async () => (await ENG.get('/api/v1/warehouse/tasks')).data?.data || [],
    refetchInterval: 20000
  });

  const counts = useMemo(() => {
    const c = { open: 0, pending: 0, in_progress: 0, done: 0, all: tasks.length };
    for (const t of tasks) {
      if (t.status === 'pending') c.pending++;
      if (t.status === 'in_progress') c.in_progress++;
      if (t.status === 'done') c.done++;
      if (t.status !== 'done' && t.status !== 'cancelled') c.open++;
    }
    return c;
  }, [tasks]);

  const rows = useMemo(() => {
    if (tab === 'all') return tasks;
    if (tab === 'open') return tasks.filter(t => t.status !== 'done' && t.status !== 'cancelled');
    return tasks.filter(t => t.status === tab);
  }, [tasks, tab]);

  const setStatus = async (task, status) => {
    setBusy(task.code);
    setErr(null);
    try {
      await ENG.patch(`/api/v1/warehouse/tasks/${task.code}`, { status });
      refetch();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3 g-2">
        <Col>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faClipboardList} className="text-primary me-2" />
            Warehouse tasks
          </h4>
          <div className="text-body-tertiary small">
            Pick / repack / put-away to-dos — pick tasks are created automatically when
            stock is reserved for an order.
          </div>
        </Col>
        <Col xs="auto">
          <Button variant="link" size="sm" className="text-body" onClick={() => refetch()}>
            <FontAwesomeIcon icon={faRotate} spin={isFetching} className="me-1" /> Refresh
          </Button>
        </Col>
      </Row>

      {err && <Alert variant="danger" dismissible onClose={() => setErr(null)} className="py-2">{err}</Alert>}

      {/* status tabs */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`btn btn-sm text-nowrap ${tab === t.key ? 'btn-primary' : 'btn-outline-secondary'}`}
          >
            {t.label} <span className="ms-1 opacity-75">({counts[t.key] ?? 0})</span>
          </button>
        ))}
      </div>

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="py-5 text-center"><Spinner animation="border" size="sm" /> Loading…</div>
          ) : error ? (
            <Alert variant="danger" className="m-3">{String(error.message)}</Alert>
          ) : rows.length === 0 ? (
            <div className="text-body-tertiary text-center py-5">No tasks in this view.</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle fs-9">
              <thead>
                <tr className="text-body-tertiary">
                  <th className="ps-3">Task</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Order / Client</th>
                  <th>Items</th>
                  <th className="text-end">Meters</th>
                  <th>Status</th>
                  <th className="text-end pe-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(t => {
                  const srcs = t.sources || [];
                  const totalM = srcs.reduce((s, x) => s + (Number(x.meters) || 0), 0);
                  return (
                    <tr key={t.code}>
                      <td className="ps-3 font-monospace">{t.code}</td>
                      <td>
                        <Badge bg={TYPE_COLOR[t.type] || 'secondary'} className="text-uppercase">
                          {t.type}
                        </Badge>
                      </td>
                      <td>{t.title || '—'}</td>
                      <td>
                        {t.forOrderCode || t.forOrderId ? (
                          <div className="d-flex flex-column">
                            <span className="font-monospace fs-10">{t.forOrderCode || t.forOrderId}</span>
                            <span className="text-body-tertiary fs-10">{t.customerName || 'STOCK'}</span>
                          </div>
                        ) : (
                          <span className="text-body-tertiary">{t.customerName || '—'}</span>
                        )}
                      </td>
                      <td>
                        {srcs.length ? (
                          <span className="fs-10 font-monospace" title={srcs.map(s => s.serial).join(', ')}>
                            {srcs.slice(0, 3).map(s => s.serial).join(', ')}
                            {srcs.length > 3 ? ` +${srcs.length - 3}` : ''}
                          </span>
                        ) : (
                          <span className="text-body-tertiary">—</span>
                        )}
                      </td>
                      <td className="text-end">{totalM ? `${fmt(totalM)} m` : '—'}</td>
                      <td>
                        <Badge bg={STATUS_COLOR[t.status] || 'secondary'}>
                          {t.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="text-end pe-3 white-space-nowrap">
                        {t.status !== 'done' && t.status !== 'cancelled' && (
                          <>
                            {t.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline-info"
                                className="me-1 py-0 px-2"
                                disabled={busy === t.code}
                                onClick={() => setStatus(t, 'in_progress')}
                                title="Start"
                              >
                                <FontAwesomeIcon icon={faPlay} />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="me-1 py-0 px-2"
                              disabled={busy === t.code}
                              onClick={() => setStatus(t, 'done')}
                              title="Done"
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              className="py-0 px-2"
                              disabled={busy === t.code}
                              onClick={() => setStatus(t, 'cancelled')}
                              title="Cancel"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
